import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const commands = [
	{ id: 'about', label: 'About', section: 'Navigate', href: '#about' },
	{ id: 'work', label: 'Work Experience', section: 'Navigate', href: '#work' },
	{ id: 'tech', label: 'Technologies', section: 'Navigate', href: '#tech' },
	{ id: 'projects', label: 'Projects', section: 'Navigate', href: '#projects' },
	{ id: 'github', label: 'GitHub', section: 'Navigate', href: '#github' },
	{ id: 'testimonials', label: 'Testimonials', section: 'Navigate', href: '#testimonials' },
	{ id: 'contact', label: 'Contact', section: 'Navigate', href: '#contact' },
	{ id: 'linkedin', label: 'LinkedIn Profile', section: 'Links', href: 'https://in.linkedin.com/in/ersachinsaurav', external: true },
	{ id: 'github-ext', label: 'GitHub Profile', section: 'Links', href: 'https://github.com/ersachinsaurav', external: true },
	{ id: 'email', label: 'Send Email', section: 'Links', href: 'mailto:hello@sachinsaurav.dev', external: true },
];

const CommandPalette = () => {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [selectedIndex, setSelectedIndex] = useState(0);
	const inputRef = useRef(null);
	const listRef = useRef(null);

	const filtered = commands.filter(
		(cmd) => cmd.label.toLowerCase().includes(query.toLowerCase())
	);

	const groupedResults = filtered.reduce((groups, cmd) => {
		if (!groups[cmd.section]) groups[cmd.section] = [];
		groups[cmd.section].push(cmd);
		return groups;
	}, {});

	const flatFiltered = Object.values(groupedResults).flat();

	const toggle = useCallback(() => {
		setOpen(prev => !prev);
		setQuery('');
		setSelectedIndex(0);
	}, []);

	useEffect(() => {
		const handler = (e) => {
			if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
				e.preventDefault();
				toggle();
			}
			if (e.key === 'Escape' && open) {
				setOpen(false);
			}
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, [open, toggle]);

	useEffect(() => {
		if (open) inputRef.current?.focus();
	}, [open]);

	useEffect(() => {
		setSelectedIndex(0);
	}, [query]);

	const executeCommand = (cmd) => {
		if (cmd.external) {
			window.open(cmd.href, '_blank', 'noopener,noreferrer');
		} else {
			document.querySelector(cmd.href)?.scrollIntoView({ behavior: 'smooth' });
		}
		setOpen(false);
	};

	const handleKeyDown = (e) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setSelectedIndex(i => Math.min(i + 1, flatFiltered.length - 1));
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setSelectedIndex(i => Math.max(i - 1, 0));
		} else if (e.key === 'Enter' && flatFiltered[selectedIndex]) {
			executeCommand(flatFiltered[selectedIndex]);
		}
	};

	return (
		<AnimatePresence>
			{open && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.15 }}
					className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] px-4 bg-black/30 backdrop-blur-sm"
					onClick={() => setOpen(false)}>
					<motion.div
						initial={{ opacity: 0, scale: 0.98, y: -10 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.98, y: -10 }}
						transition={{ duration: 0.15 }}
						className="bg-warm rounded-xl shadow-2xl border border-border w-full max-w-lg overflow-hidden"
						onClick={(e) => e.stopPropagation()}>
						<div className="flex items-center gap-3 px-4 border-b border-border">
							<svg className="w-5 h-5 text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
							</svg>
							<input
								ref={inputRef}
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								onKeyDown={handleKeyDown}
								placeholder="Type a command or search..."
								className="flex-1 py-3.5 text-[15px] text-primary placeholder:text-muted/50 bg-transparent outline-none"
							/>
							<kbd className="hidden sm:inline-flex items-center px-2 py-0.5 bg-surface border border-border rounded text-[11px] text-muted font-mono">
								ESC
							</kbd>
						</div>

						<div ref={listRef} className="max-h-72 overflow-y-auto p-2">
							{flatFiltered.length === 0 ? (
								<p className="text-muted text-[14px] text-center py-6">No results found</p>
							) : (
								Object.entries(groupedResults).map(([section, items]) => (
									<div key={section}>
										<p className="text-muted text-[11px] font-semibold uppercase tracking-wider px-3 pt-3 pb-1.5">
											{section}
										</p>
										{items.map((cmd) => {
											const globalIndex = flatFiltered.indexOf(cmd);
											return (
												<button
													key={cmd.id}
													onClick={() => executeCommand(cmd)}
													onMouseEnter={() => setSelectedIndex(globalIndex)}
													className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors ${
														selectedIndex === globalIndex
															? 'bg-accent text-white'
															: 'text-primary hover:bg-surface'
													}`}>
													<span className="text-[14px] font-medium">{cmd.label}</span>
													{cmd.external && (
														<svg className={`w-3.5 h-3.5 ${selectedIndex === globalIndex ? 'text-white/70' : 'text-muted'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
														</svg>
													)}
												</button>
											);
										})}
									</div>
								))
							)}
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
};

export default CommandPalette;
